import React from 'react';
import { Button, Badge, Spin, Tabs, Tag, Typography, Collapse, Popconfirm, message } from 'antd';
import { RefreshCw, Database, Zap, Trash2, AlertTriangle } from 'lucide-react';

const { Text, Title } = Typography;
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const JWT_KEY  = 'drupi_social_jwt';

// Los endpoints /drupi/admin/* ahora están gateados por require_admin en el
// backend, así que toda request manda el JWT (validado por OAuth).
const authHeaders = (): Record<string, string> => {
  const jwt = localStorage.getItem(JWT_KEY);
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const ENTITIES: { key: string; label: string; url: string; description: string }[] = [
  {
    key: 'accounts',
    label: 'Social Accounts',
    url: '/drupi/admin/accounts',
    description: 'Cuentas TikTok/IG conectadas con user_id, tokens y puntos',
  },
  {
    key: 'catalog',
    label: 'Catalog Videos',
    url: '/drupi/catalog',
    description: 'Videos subidos al servidor para publicar',
  },
  {
    key: 'publish-tasks',
    label: 'Publish Tasks',
    url: '/drupi/publish-tasks?limit=100',
    description: 'Tareas de publicación encoladas o completadas',
  },
  {
    key: 'tracked-videos',
    label: 'Tracked Videos',
    url: '/drupi/videos?active_only=false',
    description: 'Videos publicados monitoreados para métricas',
  },
  {
    key: 'ranking',
    label: 'Ranking',
    url: '/drupi/ranking',
    description: 'Ranking agrupado por user_id cross-platform',
  },
];

// ── Row renderer ─────────────────────────────────────────────────────────────

const SENSITIVE = new Set(['access_token', 'refresh_token', 'token']);

function renderValue(key: string, val: unknown): React.ReactNode {
  if (SENSITIVE.has(key) && typeof val === 'string' && val.length > 12) {
    return <Text type='secondary'>{val.slice(0, 8)}…<Tag color='orange'>token</Tag></Text>;
  }
  if (val === null || val === undefined) return <Text type='secondary'>—</Text>;
  if (typeof val === 'boolean') return <Tag color={val ? 'green' : 'red'}>{String(val)}</Tag>;
  if (Array.isArray(val)) return <Text code>{JSON.stringify(val)}</Text>;
  if (typeof val === 'object') return <Text code style={{ fontSize: 11 }}>{JSON.stringify(val)}</Text>;
  const str = String(val);
  // Dates
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    try {
      return <Text>{new Date(str).toLocaleString('es-AR')}</Text>;
    } catch { /* noop */ }
  }
  return <Text>{str}</Text>;
}

const RecordCard: React.FC<{ record: Record<string, unknown>; index: number }> = ({ record, index }) => {
  // Pick a human-readable title
  const title =
    (record['username'] as string) ||
    (record['_id'] as string) ||
    (record['user_id'] as string) ||
    `#${index + 1}`;

  const subtitle =
    record['platform']
      ? `${record['platform']} · ${record['social_id'] || ''}`
      : record['user_id'] as string || '';

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 10,
        marginBottom: 8,
        overflow: 'hidden',
      }}
    >
      <Collapse
        ghost
        items={[
          {
            key: '1',
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{title}</span>
                {subtitle && (
                  <Text type='secondary' style={{ fontSize: 12 }}>{subtitle}</Text>
                )}
                {record['points'] !== undefined && (
                  <Tag color='gold'>{String(record['points'])} pts</Tag>
                )}
                {!!record['disconnected'] && <Tag color='red'>disconnected</Tag>}
                {!!record['status'] && <Tag>{record['status'] as string}</Tag>}
              </div>
            ),
            children: (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <tbody>
                  {Object.entries(record).map(([k, v]) => (
                    <tr key={k} style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                      <td
                        style={{
                          padding: '5px 12px',
                          color: '#6b7280',
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          width: 180,
                          verticalAlign: 'top',
                        }}
                      >
                        {k}
                      </td>
                      <td style={{ padding: '5px 12px', wordBreak: 'break-all' }}>
                        {renderValue(k, v)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ),
          },
        ]}
      />
    </div>
  );
};

// ── Entity panel ─────────────────────────────────────────────────────────────

const EntityPanel: React.FC<{ url: string; description: string }> = ({ url, description }) => {
  const [data, setData] = React.useState<Record<string, unknown>[] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [lastFetch, setLastFetch] = React.useState<Date | null>(null);

  const fetch_ = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}${url}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(Array.isArray(json) ? json : [json]);
      setLastFetch(new Date());
    } catch (e: any) {
      setError(e.message || 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, [url]);

  React.useEffect(() => { fetch_(); }, [fetch_]);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Text type='secondary' style={{ flex: 1, fontSize: 13 }}>{description}</Text>
        {data && (
          <Badge
            count={data.length}
            style={{ backgroundColor: '#3b4a8a' }}
            overflowCount={999}
          />
        )}
        <Button
          size='small'
          icon={<RefreshCw size={13} />}
          onClick={fetch_}
          loading={loading}
        >
          Refrescar
        </Button>
      </div>

      {lastFetch && (
        <Text type='secondary' style={{ fontSize: 11, display: 'block', marginBottom: 12 }}>
          Actualizado: {lastFetch.toLocaleTimeString('es-AR')} · endpoint: {API_BASE}{url}
        </Text>
      )}

      {/* Content */}
      {loading && !data && (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      )}

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: 8,
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
          color: '#dc2626', fontSize: 13,
        }}>
          Error: {error}
        </div>
      )}

      {data && data.length === 0 && (
        <div style={{
          padding: '24px 16px', textAlign: 'center',
          color: '#9ca3af', fontSize: 13,
          background: '#f9fafb', borderRadius: 8,
          border: '1px dashed rgba(0,0,0,0.1)',
        }}>
          Sin registros
        </div>
      )}

      {data && data.map((record, i) => (
        <RecordCard key={i} record={record} index={i} />
      ))}
    </div>
  );
};

// ── Admin Actions ─────────────────────────────────────────────────────────────

const ADMIN_ACTIONS: { key: string; label: string; url: string; description: string }[] = [
  {
    key: 'fetch-publication-metrics',
    label: 'Actualizar métricas de publicaciones',
    url: '/drupi/admin/fetch-publication-metrics-now',
    description: 'Consulta la API de TikTok/IG y guarda métricas (likes, views, shares) de todos los videos publicados.',
  },
  {
    key: 'recalculate-points',
    label: 'Recalcular puntos',
    url: '/drupi/admin/recalculate-points-now',
    description: 'Recalcula los puntos de todos los influencers en base a las últimas métricas.',
  },
  {
    key: 'fetch-tracked-metrics',
    label: 'Actualizar métricas de tracked videos',
    url: '/drupi/admin/fetch-metrics-now',
    description: 'Corre el MetricsFetcher para todos los videos activos tracked.',
  },
];

const ActionButton: React.FC<{ action: typeof ADMIN_ACTIONS[0] }> = ({ action }) => {
  const [loading, setLoading] = React.useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}${action.url}`, { method: 'POST', headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      message.success(`${action.label} — listo. ${JSON.stringify(json)}`);
    } catch (e: any) {
      message.error(`Error: ${e.message || 'Error desconocido'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16,
      padding: '12px 16px',
      background: '#fff',
      border: '1px solid rgba(0,0,0,0.08)',
      borderRadius: 10,
      marginBottom: 8,
    }}>
      <div style={{ flex: 1 }}>
        <Text strong style={{ fontSize: 14 }}>{action.label}</Text>
        <Text type='secondary' style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
          {action.description}
        </Text>
      </div>
      <Button
        icon={<Zap size={13} />}
        loading={loading}
        onClick={handleClick}
        type='primary'
        size='small'
      >
        Ejecutar
      </Button>
    </div>
  );
};

// ── Danger zone: destructive one-shot actions ────────────────────────────────
// A diferencia de ActionButton (que dispara al primer click), estas van SIEMPRE
// gateadas con Popconfirm — borran datos sin vuelta atrás.

const DeleteAllPrizesButton: React.FC = () => {
  const [loading, setLoading] = React.useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/drupi/admin/prizes/all`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      message.success(`Premios eliminados: ${json.deleted ?? 0}`);
    } catch (e: any) {
      message.error(`Error: ${e.message || 'Error desconocido'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16,
      padding: '12px 16px',
      background: 'rgba(239,68,68,0.04)',
      border: '1px solid rgba(239,68,68,0.25)',
      borderRadius: 10,
      marginBottom: 8,
    }}>
      <div style={{ flex: 1 }}>
        <Text strong style={{ fontSize: 14 }}>Eliminar todos los premios</Text>
        <Text type='secondary' style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
          Borra TODOS los premios de la base (todas las marcas, todos los meses). No toca el
          estado del worker mensual. Esta acción no se puede deshacer.
        </Text>
      </div>
      <Popconfirm
        title='¿Eliminar TODOS los premios?'
        description='Se borran todos los premios de todas las marcas y meses. No se puede deshacer.'
        okText='Sí, eliminar todo'
        cancelText='Cancelar'
        okButtonProps={{ danger: true }}
        onConfirm={handleDelete}
      >
        <Button danger type='primary' icon={<Trash2 size={13} />} loading={loading} size='small'>
          Eliminar todo
        </Button>
      </Popconfirm>
    </div>
  );
};

// ── Exceptions panel ─────────────────────────────────────────────────────────

type ExceptionRecord = {
  _id: string;
  sk: string;
  kind: string;
  source: string;
  message: string;
  traceback: string;
  context: Record<string, unknown>;
  createdAt: string;
};

const KIND_COLOR: Record<string, string> = {
  UNHANDLED_500: 'red',
  CAUGHT:        'orange',
  MANUAL:        'blue',
};

const ExceptionCard: React.FC<{ record: ExceptionRecord; onDeleted: () => void }> = ({ record, onDeleted }) => {
  const [deleting, setDeleting] = React.useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const url = `${API_BASE}/drupi/admin/exceptions?sk=${encodeURIComponent(record.sk)}`;
      const res = await fetch(url, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      message.success('Excepción borrada');
      onDeleted();
    } catch (e: any) {
      message.error(`No pude borrar: ${e.message || 'error'}`);
    } finally {
      setDeleting(false);
    }
  };

  const ts = (() => {
    try { return new Date(record.createdAt).toLocaleString('es-AR'); }
    catch { return record.createdAt; }
  })();

  const preview = (record.message || '').slice(0, 120);

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 10,
        marginBottom: 8,
        overflow: 'hidden',
      }}
    >
      <Collapse
        ghost
        items={[
          {
            key: '1',
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                <Tag color={KIND_COLOR[record.kind] || 'default'}>{record.kind}</Tag>
                <Text type='secondary' style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{ts}</Text>
                <Text style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {preview}
                </Text>
                <Popconfirm
                  title='¿Borrar esta excepción?'
                  okText='Borrar'
                  cancelText='Cancelar'
                  okButtonProps={{ danger: true }}
                  onConfirm={(e) => { e?.stopPropagation(); handleDelete(); }}
                  onCancel={(e) => e?.stopPropagation()}
                >
                  <Button
                    size='small'
                    danger
                    icon={<Trash2 size={12} />}
                    loading={deleting}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Popconfirm>
              </div>
            ),
            children: (
              <div style={{ fontSize: 13 }}>
                <div style={{ padding: '4px 12px', color: '#6b7280' }}><b>source:</b> {record.source}</div>
                <div style={{ padding: '4px 12px', color: '#6b7280' }}><b>message:</b></div>
                <pre style={{
                  background: '#f9fafb', margin: '0 12px 8px', padding: 8,
                  borderRadius: 6, fontSize: 12, whiteSpace: 'pre-wrap',
                }}>{record.message || '—'}</pre>
                {record.traceback && (
                  <>
                    <div style={{ padding: '4px 12px', color: '#6b7280' }}><b>traceback:</b></div>
                    <pre style={{
                      background: '#f9fafb', margin: '0 12px 8px', padding: 8,
                      borderRadius: 6, fontSize: 11, whiteSpace: 'pre-wrap',
                      maxHeight: 320, overflow: 'auto',
                    }}>{record.traceback}</pre>
                  </>
                )}
                {record.context && Object.keys(record.context).length > 0 && (
                  <>
                    <div style={{ padding: '4px 12px', color: '#6b7280' }}><b>context:</b></div>
                    <pre style={{
                      background: '#f9fafb', margin: '0 12px 8px', padding: 8,
                      borderRadius: 6, fontSize: 11, whiteSpace: 'pre-wrap',
                    }}>{JSON.stringify(record.context, null, 2)}</pre>
                  </>
                )}
                <div style={{ padding: '4px 12px', color: '#9ca3af', fontSize: 10 }}>
                  sk: {record.sk}
                </div>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

const ExceptionsPanel: React.FC = () => {
  const [data, setData] = React.useState<ExceptionRecord[] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [lastFetch, setLastFetch] = React.useState<Date | null>(null);

  const url = '/drupi/admin/exceptions?limit=200';

  const fetch_ = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}${url}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      // Backend returns the array directly; sk lives inside each item as `sk` (and `_id`).
      const items: ExceptionRecord[] = (Array.isArray(json) ? json : []).map((it: any) => ({
        _id:       it._id ?? it.sk ?? '',
        sk:        it.sk ?? it._id ?? '',
        kind:      it.kind ?? 'CAUGHT',
        source:    it.source ?? '',
        message:   it.message ?? '',
        traceback: it.traceback ?? '',
        context:   it.context ?? {},
        createdAt: it.createdAt ?? '',
      }));
      setData(items);
      setLastFetch(new Date());
    } catch (e: any) {
      setError(e.message || 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { fetch_(); }, [fetch_]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Text type='secondary' style={{ flex: 1, fontSize: 13 }}>
          Errores capturados (try/except) y 500 no manejados. Borralos cuando los analices.
        </Text>
        {data && (
          <Badge count={data.length} style={{ backgroundColor: '#dc2626' }} overflowCount={999} />
        )}
        <Button size='small' icon={<RefreshCw size={13} />} onClick={fetch_} loading={loading}>
          Refrescar
        </Button>
      </div>

      {lastFetch && (
        <Text type='secondary' style={{ fontSize: 11, display: 'block', marginBottom: 12 }}>
          Actualizado: {lastFetch.toLocaleTimeString('es-AR')} · endpoint: {API_BASE}{url}
        </Text>
      )}

      {loading && !data && (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      )}

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: 8,
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
          color: '#dc2626', fontSize: 13,
        }}>
          Error: {error}
        </div>
      )}

      {data && data.length === 0 && (
        <div style={{
          padding: '24px 16px', textAlign: 'center',
          color: '#9ca3af', fontSize: 13,
          background: '#f9fafb', borderRadius: 8,
          border: '1px dashed rgba(0,0,0,0.1)',
        }}>
          Sin excepciones registradas
        </div>
      )}

      {data && data.map(rec => (
        <ExceptionCard key={rec.sk} record={rec} onDeleted={fetch_} />
      ))}
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

export const AdminPage: React.FC = () => {
  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
        <Database size={22} color='#3b4a8a' />
        <Title level={3} style={{ margin: 0, color: '#1e293b' }}>
          Admin — Drupi DB
        </Title>
        <Tag color='orange' style={{ marginLeft: 8 }}>STAGING</Tag>
      </div>

      <Text type='secondary' style={{ display: 'block', marginBottom: 24, fontSize: 14 }}>
        Vista de solo lectura de todas las entidades de la base de datos de Drupi.
        Usá para verificar el estado real de los datos durante pruebas.
      </Text>

      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Zap size={16} color='#3b4a8a' />
          <Title level={5} style={{ margin: 0, color: '#1e293b' }}>Acciones</Title>
        </div>
        {ADMIN_ACTIONS.map(action => (
          <ActionButton key={action.key} action={action} />
        ))}
      </div>

      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <AlertTriangle size={16} color='#dc2626' />
          <Title level={5} style={{ margin: 0, color: '#dc2626' }}>Zona peligrosa</Title>
        </div>
        <DeleteAllPrizesButton />
      </div>

      <Tabs
        type='card'
        items={[
          ...ENTITIES.map(entity => ({
            key: entity.key,
            label: entity.label,
            children: (
              <div style={{ padding: '16px 0' }}>
                <EntityPanel url={entity.url} description={entity.description} />
              </div>
            ),
          })),
          {
            key: 'exceptions',
            label: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={13} /> Exceptions
              </span>
            ),
            children: (
              <div style={{ padding: '16px 0' }}>
                <ExceptionsPanel />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
